import React from 'react';
import { X, Check, Minus, ArrowUpRight } from 'lucide-react';
import { PCCase } from '../../types';
import { Badge } from '../ui';
import { useAppStore } from '../../context/store';
import {
  formatDimension,
  formatDimensionValue,
  formatWeight,
  calculateVolumeLiters,
  getAmazonSearchUrl,
} from '../../utils/conversions';
import { getSeriesColor } from '../../utils/seriesColors';
import { sortFormFactors } from '../../utils/formFactors';

interface ComparisonTableProps {
  cases: PCCase[];
}

const Empty: React.FC = () => <Minus size={16} className="mx-auto text-muted" aria-label="None" />;

export const ComparisonTable: React.FC<ComparisonTableProps> = ({ cases }) => {
  const { removeFromComparison, comparison } = useAppStore();
  const unit = comparison.unitSystem;
  const dimUnit = unit === 'metric' ? 'mm' : 'in';
  const weightUnit = unit === 'metric' ? 'kg' : 'lb';

  const volumes = cases.filter((c) => c.dimensions).map((c) => calculateVolumeLiters(c.dimensions));

  // Best value per row, highlighted in the accent color
  const stats = {
    maxGpu: Math.max(...cases.map((c) => c.maxGpuLength)),
    maxCooler: Math.max(...cases.map((c) => c.maxCpuCoolerHeight)),
    minVolume: volumes.length > 0 ? Math.min(...volumes) : null,
    minWeight: Math.min(...cases.map((c) => c.weight)),
  };

  const highlight = (value: number, best: number | null) =>
    cases.length > 1 && value === best ? 'text-accent-ink font-semibold' : '';

  const specs: { label: string; getValue: (c: PCCase) => React.ReactNode }[] = [
    {
      label: 'Dimensions (H × W × D)',
      getValue: (c) => c.dimensions ? (
        <span className="font-mono text-sm">
          {formatDimensionValue(c.dimensions.height, dimUnit)} × {formatDimensionValue(c.dimensions.width, dimUnit)} × {formatDimensionValue(c.dimensions.depth, dimUnit)} {dimUnit}
        </span>
      ) : <Empty />,
    },
    {
      label: 'Volume',
      getValue: (c) => {
        if (!c.dimensions) {
          return <Empty />;
        }
        const vol = calculateVolumeLiters(c.dimensions);
        return <span className={`font-mono ${highlight(vol, stats.minVolume)}`}>{vol.toFixed(1)} L</span>;
      },
    },
    {
      label: 'Weight',
      getValue: (c) => (
        <span className={`font-mono ${highlight(c.weight, stats.minWeight)}`}>
          {formatWeight(c.weight, weightUnit)}
        </span>
      ),
    },
    {
      label: 'Size category',
      getValue: (c) => <Badge>{c.sizeCategory}</Badge>,
    },
    {
      label: 'Motherboard',
      getValue: (c) => (
        <div className="flex flex-wrap justify-center gap-1">
          {sortFormFactors(c.formFactorSupport).map((ff) => (
            <Badge key={ff} size="sm">{ff}</Badge>
          ))}
        </div>
      ),
    },
    {
      label: 'Max GPU length',
      getValue: (c) => (
        <span className={`font-mono ${highlight(c.maxGpuLength, stats.maxGpu)}`}>
          {formatDimension(c.maxGpuLength, dimUnit)}
        </span>
      ),
    },
    {
      label: 'Max CPU cooler',
      getValue: (c) => (
        <span className={`font-mono ${highlight(c.maxCpuCoolerHeight, stats.maxCooler)}`}>
          {formatDimension(c.maxCpuCoolerHeight, dimUnit)}
        </span>
      ),
    },
    {
      label: 'Max PSU length',
      getValue: (c) => <span className="font-mono">{formatDimension(c.maxPsuLength, dimUnit)}</span>,
    },
    {
      label: 'Side panel',
      getValue: (c) => c.sidePanel.join(', ') || <Empty />,
    },
    {
      label: '3.5" bays',
      getValue: (c) => <span className="font-mono">{c.driveBays.internal35}</span>,
    },
    {
      label: '2.5" bays',
      getValue: (c) => <span className="font-mono">{c.driveBays.internal25}</span>,
    },
    {
      label: 'USB 3.0 ports',
      getValue: (c) => c.ioPanel.usb3 ? <span className="font-mono">{c.ioPanel.usb3}</span> : <Empty />,
    },
    {
      label: 'USB-C ports',
      getValue: (c) => c.ioPanel.usbC ? <span className="font-mono">{c.ioPanel.usbC}</span> : <Empty />,
    },
    {
      label: 'Audio jack',
      getValue: (c) =>
        c.ioPanel.audio
          ? <Check size={16} className="mx-auto text-fg" aria-label="Yes" />
          : <X size={16} className="mx-auto text-muted" aria-label="No" />,
    },
    {
      label: 'Fan mounts',
      getValue: (c) => c.fanMounts.length > 0 ? (
        <div className="space-y-1 text-sm">
          {c.fanMounts.map((fm, i) => (
            <div key={i}>
              <span className="capitalize text-muted">{fm.location}</span>{' '}
              <span className="font-mono">{fm.maxFans}× {fm.sizes.join('/')}</span>
            </div>
          ))}
        </div>
      ) : <Empty />,
    },
    {
      label: 'Radiator support',
      getValue: (c) => {
        const radiators = c.fanMounts.filter((fm) => fm.radiatorSupport && fm.radiatorSupport.length > 0);
        return radiators.length > 0 ? (
          <div className="space-y-1 text-sm">
            {radiators.map((fm, i) => (
              <div key={i}>
                <span className="capitalize text-muted">{fm.location}</span>{' '}
                <span className="font-mono">{Math.max(...(fm.radiatorSupport || []))} mm</span>
              </div>
            ))}
          </div>
        ) : <Empty />;
      },
    },
    {
      label: 'Colors',
      getValue: (c) => (
        <div className="flex flex-wrap justify-center gap-1">
          {c.colors.map((color) => (
            <Badge key={color} size="sm">{color}</Badge>
          ))}
        </div>
      ),
    },
    {
      label: 'Release year',
      getValue: (c) => c.releaseYear > 0 ? <span className="font-mono">{c.releaseYear}</span> : <Empty />,
    },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px] border-collapse">
        <thead>
          <tr className="border-b border-line">
            <th scope="col" className="w-48 px-4 py-4 text-left align-bottom font-mono text-xs font-normal uppercase tracking-label text-muted">
              Specification
            </th>
            {cases.map((pcCase, index) => {
              const image = pcCase.thumbnailUrl || pcCase.imageUrl;
              const shopLink = pcCase.referralLinks?.[0];
              return (
                <th key={pcCase.id} scope="col" className="min-w-[180px] px-4 pb-4 pt-0 align-top font-normal">
                  <div className="flex flex-col items-center gap-2">
                    <span className="block h-1 w-full rounded-b" style={{ backgroundColor: getSeriesColor(index) }} aria-hidden="true" />
                    {image && (
                      <img
                        src={image}
                        alt={`${pcCase.manufacturer} ${pcCase.name}`}
                        className="mt-2 h-28 w-28 object-contain mix-blend-multiply dark:mix-blend-normal"
                      />
                    )}
                    <span className="font-mono text-2xs uppercase tracking-label text-muted">{pcCase.manufacturer}</span>
                    <span className="text-base font-semibold tracking-tight text-fg">{pcCase.name}</span>
                    <div className="flex items-center gap-1">
                      <a
                        href={shopLink?.url ?? getAmazonSearchUrl(pcCase.manufacturer, pcCase.name)}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        className="inline-flex h-9 items-center gap-1 px-2 text-sm text-muted hover:text-fg transition-colors"
                      >
                        {shopLink?.retailer ?? 'Amazon'}
                        <ArrowUpRight size={13} />
                      </a>
                      <button
                        type="button"
                        onClick={() => removeFromComparison(pcCase.id)}
                        aria-label={`Remove ${pcCase.manufacturer} ${pcCase.name} from comparison`}
                        className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm text-muted hover:bg-surface-2 hover:text-fg transition-colors"
                      >
                        <X size={14} />
                        Remove
                      </button>
                    </div>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {specs.map((spec) => (
            <tr key={spec.label} className="border-b border-line last:border-0">
              <th scope="row" className="px-4 py-3 text-left text-sm font-normal text-muted">{spec.label}</th>
              {cases.map((pcCase) => (
                <td key={pcCase.id} className="px-4 py-3 text-center text-sm text-fg">
                  {spec.getValue(pcCase)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
